import type { QueryClient } from '@tanstack/react-query';

import type { MarketDefinitionT } from '@web/routes/trade/-workstation/types';
import { isAssetNotFoundError } from './market-feed/marketFeedData';
import {
  marketDefinitionQueryOptions,
  marketsQueryOptions,
} from './market-feed/marketFeedQueryOptions';

interface TradeWorkbenchRouteDataT {
  market: MarketDefinitionT | null;
  markets: MarketDefinitionT[];
  requestedAsset: string;
}

export async function loadTradeWorkbenchRouteData(input: {
  asset: string;
  queryClient: QueryClient;
}): Promise<TradeWorkbenchRouteDataT> {
  const requestedAsset = input.asset.trim().toUpperCase();
  const cachedMarkets = input.queryClient.getQueryData<MarketDefinitionT[]>([
    'market-feed',
    'markets',
  ]);

  if (cachedMarkets !== undefined) {
    const cachedMarket = readActiveMarket({
      markets: cachedMarkets,
      requestedAsset,
    });

    return {
      market: cachedMarket,
      markets: cachedMarkets,
      requestedAsset,
    };
  }

  try {
    const market = await input.queryClient.ensureQueryData(
      marketDefinitionQueryOptions(requestedAsset),
    );

    return {
      market,
      markets: [market],
      requestedAsset,
    };
  } catch (error) {
    if (!isAssetNotFoundError(error)) {
      throw error;
    }

    const markets = await input.queryClient.ensureQueryData(
      marketsQueryOptions(),
    );
    const market = readActiveMarket({
      markets,
      requestedAsset,
    });

    return {
      market,
      markets,
      requestedAsset,
    };
  }
}

function readActiveMarket(input: {
  markets: MarketDefinitionT[];
  requestedAsset: string;
}) {
  const requestedAsset = input.requestedAsset.trim().toUpperCase();

  for (const market of input.markets) {
    if (market.asset === requestedAsset) {
      return market;
    }
  }

  return input.markets[0] ?? null;
}
